"use client";

import { type QuantityTextDeclaration, quantityText } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import {
  type DateFieldProps,
  type FieldBindingProps,
  type FormActionsProps,
  type InputFieldProps,
  type MultiOptionGroupFieldProps,
  type NumberFieldProps,
  type OptionGroupFieldProps,
  DateField as PureDateField,
  DurationSecondsField as PureDurationSecondsField,
  FormActions as PureFormActions,
  InputField as PureInputField,
  MultiOptionGroupField as PureMultiOptionGroupField,
  NumberField as PureNumberField,
  OptionGroupField as PureOptionGroupField,
  SelectField as PureSelectField,
  SwitchField as PureSwitchField,
  SwitchListField as PureSwitchListField,
  TextareaField as PureTextareaField,
  TimePickerField as PureTimePickerField,
  type SelectFieldProps,
  type SwitchFieldProps,
  type SwitchListFieldProps,
  type TextareaFieldProps,
  type TimePickerFieldProps,
} from "@repo/ui/form-fields";
import { cn } from "@repo/ui/lib/utils";
import { useStore } from "@tanstack/react-form";
import type { ReactNode } from "react";
import { useFieldContext, useFormContext } from "./form-context";
import { useSchemaValid } from "./use-schema-valid";

export function useFieldBinding<T>(): FieldBindingProps<T> {
  const field = useFieldContext<T>();
  return {
    value: field.state.value,
    onChange: field.handleChange,
    onBlur: field.handleBlur,
    errors: field.state.meta.errors,
    isTouched: field.state.meta.isTouched,
  };
}

export function InputField(props: Omit<InputFieldProps, keyof FieldBindingProps<string>>) {
  const binding = useFieldBinding<string>();
  return <PureInputField {...binding} value={binding.value ?? ""} {...props} />;
}

const QUANTITY_FIELDS = { duration: PureDurationSecondsField, number: PureNumberField } as const;

/** The control a numeric declaration is entered with. */
export function quantityField(declaration?: QuantityTextDeclaration) {
  return QUANTITY_FIELDS[quantityText(declaration).input];
}

type ConnectedNumberFieldProps = Omit<NumberFieldProps, keyof FieldBindingProps<number | null>> & {
  declaration?: QuantityTextDeclaration;
};

export function NumberField({ declaration, ...props }: ConnectedNumberFieldProps) {
  const binding = useFieldBinding<number | null>();
  const Field = quantityField(declaration);
  return <Field {...binding} value={binding.value ?? null} {...props} />;
}

/** Numeric controls for forms that serialize their values as strings. */
export function StringNumberField({ declaration, ...props }: ConnectedNumberFieldProps) {
  const binding = useFieldBinding<string>();
  const value = binding.value === "" ? null : Number(binding.value);
  const Field = quantityField(declaration);
  return (
    <Field
      {...binding}
      {...props}
      value={value !== null && Number.isFinite(value) ? value : null}
      onChange={(next) => binding.onChange(next === null ? "" : String(next))}
    />
  );
}

export function SelectField(props: Omit<SelectFieldProps, keyof FieldBindingProps<string>>) {
  return <PureSelectField {...useFieldBinding<string>()} {...props} />;
}

export function OptionGroupField(props: Omit<OptionGroupFieldProps, keyof FieldBindingProps<string>>) {
  return <PureOptionGroupField {...useFieldBinding<string>()} {...props} />;
}

export function MultiOptionGroupField(props: Omit<MultiOptionGroupFieldProps, keyof FieldBindingProps<string[]>>) {
  return <PureMultiOptionGroupField {...useFieldBinding<string[]>()} {...props} />;
}

export function TextareaField(props: Omit<TextareaFieldProps, keyof FieldBindingProps<string>>) {
  return <PureTextareaField {...useFieldBinding<string>()} {...props} />;
}

export function SwitchField(props: Omit<SwitchFieldProps, keyof FieldBindingProps<boolean>>) {
  // Default to `false` (never `undefined`). Base UI's Switch latches into
  // uncontrolled mode on first render if `checked` is undefined, then ignores
  // all subsequent `checked` prop changes — breaking cross-tab sync for any
  // boolean field whose initial form value is undefined.
  const binding = useFieldBinding<boolean>();
  return <PureSwitchField {...binding} value={binding.value ?? false} {...props} />;
}

export function SwitchListField(props: Omit<SwitchListFieldProps, keyof FieldBindingProps<string[]>>) {
  const binding = useFieldBinding<string[]>();
  return <PureSwitchListField {...binding} value={binding.value ?? []} {...props} />;
}

export function DateField(props: Omit<DateFieldProps, keyof FieldBindingProps<Date | null>>) {
  return <PureDateField {...useFieldBinding<Date | null>()} {...props} />;
}

export function TimePickerField(props: Omit<TimePickerFieldProps, keyof FieldBindingProps<string>>) {
  return <PureTimePickerField {...useFieldBinding<string>()} {...props} />;
}

export function FormActions(props: Omit<FormActionsProps, "canSubmit" | "isPristine" | "isSubmitting" | "isComplete">) {
  const form = useFormContext();
  const canSubmit = useStore(form.store, (s) => s.canSubmit);
  const isPristine = useStore(form.store, (s) => s.isPristine);
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
  const isComplete = useSchemaValid(form);
  return (
    <PureFormActions
      canSubmit={canSubmit}
      isPristine={isPristine}
      isSubmitting={isSubmitting}
      isComplete={isComplete}
      {...props}
    />
  );
}

/** Muted while the schema fails but still pressable: pressing it is how the author finds out what is missing. */
export function SubmitButton({ children, disabled }: { children: ReactNode; disabled?: boolean }) {
  const form = useFormContext();
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
  const isComplete = useSchemaValid(form);
  return (
    <Button type="submit" disabled={isSubmitting || disabled} className={cn(!isComplete && "opacity-50")}>
      {children}
    </Button>
  );
}
